import { useEffect, useRef } from 'react';

const DEFAULT_ACTIVE_MS = 4_000;
const DEFAULT_BACKGROUND_MS = 45_000;

function withJitter(ms: number, pct = 0.1): number {
  const delta = ms * pct;
  return Math.max(250, Math.round(ms + (Math.random() * 2 - 1) * delta));
}

export type AdaptivePollingOptions = {
  /** Interval while the tab is visible. Default: 4000ms. */
  activeMs?: number;
  /**
   * Interval while the tab is hidden when pauseWhenHidden is false.
   * Default: 45000ms.
   */
  backgroundMs?: number;
  /** When true (default), polling pauses entirely while the document is hidden. */
  pauseWhenHidden?: boolean;
  /** Run the callback once immediately when polling starts / becomes enabled. */
  runImmediately?: boolean;
};

/**
 * Schedules an async callback on a visibility-aware cadence with light jitter
 * to avoid synchronized bursts across clients.
 */
export function useVisibilityAdaptiveInterval(
  callback: () => void | Promise<void>,
  enabled: boolean,
  options: AdaptivePollingOptions = {}
) {
  const {
    activeMs = DEFAULT_ACTIVE_MS,
    backgroundMs = DEFAULT_BACKGROUND_MS,
    pauseWhenHidden = true,
    runImmediately = false,
  } = options;

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    let running = false;

    const clear = () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const isHidden = () =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden';

    const runCallback = async () => {
      if (cancelled || running) return;
      if (isHidden() && pauseWhenHidden) return;

      running = true;
      try {
        await callbackRef.current();
      } catch (err) {
        console.error('Adaptive polling callback failed:', err);
      } finally {
        running = false;
      }
    };

    const scheduleNext = () => {
      clear();
      if (cancelled) return;

      if (isHidden() && pauseWhenHidden) {
        return;
      }

      const base = isHidden() ? backgroundMs : activeMs;
      timeoutId = setTimeout(async () => {
        await runCallback();
        scheduleNext();
      }, withJitter(base));
    };

    const onVisibility = () => {
      if (cancelled) return;

      if (isHidden()) {
        clear();
        if (!pauseWhenHidden) {
          scheduleNext();
        }
        return;
      }

      void (async () => {
        await runCallback();
        scheduleNext();
      })();
    };

    document.addEventListener('visibilitychange', onVisibility);

    void (async () => {
      if (runImmediately) {
        await runCallback();
      }
      scheduleNext();
    })();

    return () => {
      cancelled = true;
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, activeMs, backgroundMs, pauseWhenHidden, runImmediately]);
}
